import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { formatPrice, calcDiscount } from '../utils/formatPrice';

const SORT_OPTIONS = [
  { label: 'Recently Added', value: 'recent' },
  { label: 'Price: Low → High', value: 'price_asc' },
  { label: 'Price: High → Low', value: 'price_desc' },
];

// ── Heart / Remove icon ──────────────────────────────────────────────────────
const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// ── Wishlist Item Card ────────────────────────────────────────────────────────
const WishlistCard = ({ item, onRemove }) => {
  const { addItem } = useCart();
  const [addState, setAddState] = useState('idle'); // idle | adding | done
  const [imgLoaded, setImgLoaded] = useState(false);

  const discount = calcDiscount(item.price, item.compareAtPrice);
  const savings = item.compareAtPrice > item.price ? item.compareAtPrice - item.price : 0;
  const continueOOS = item.variants?.some(v => v.continueSellingWhenOutOfStock);
  const isOOS = (item.inventory ?? 0) <= 0 && !continueOOS;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (addState !== 'idle' || isOOS) return;
    setAddState('adding');
    await new Promise(r => setTimeout(r, 350));
    addItem(item, item.variants?.[0]?.size || 'default');
    toast.success(`${item.title.split(' ').slice(0, 4).join(' ')} added to cart!`, {
      style: { borderRadius: '10px' },
    });
    setAddState('done');
    setTimeout(() => setAddState('idle'), 1800);
  };

  return (
    <div className="group bg-white rounded-2xl border border-border overflow-hidden card-hover flex flex-col sm:flex-row">
      {/* Image */}
      <Link
        to={`/product/${item.handle}`}
        className="block relative shrink-0 sm:w-44 aspect-square sm:aspect-auto bg-gray-50"
      >
        {!imgLoaded && <div className="absolute inset-0 shimmer rounded-none" />}
        <img
          src={item.images?.[0]}
          alt={item.title}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          className={`w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform duration-400
            ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
          {discount > 0 && (
            <span className="badge bg-emerald-500 text-white text-[10px] px-2 py-0.5">-{discount}%</span>
          )}
          {isOOS && (
            <span className="badge bg-gray-400 text-white text-[10px] px-2 py-0.5">Out of Stock</span>
          )}
        </div>
      </Link>

      {/* Info */}
      <div className="flex flex-col flex-1 p-4 gap-2 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-0.5">
              {item.vendor}
            </p>
            <Link to={`/product/${item.handle}`}>
              <h3 className="text-sm font-semibold text-ink line-clamp-2 leading-snug hover:text-primary transition-colors">
                {item.title}
              </h3>
            </Link>
          </div>

          {/* Remove button */}
          <button
            onClick={() => onRemove(item.id)}
            title="Remove from wishlist"
            className="shrink-0 w-8 h-8 rounded-full border border-border flex items-center justify-center
                       text-ink-muted hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all"
          >
            <TrashIcon />
          </button>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mt-auto">
          <span className="text-base font-bold text-ink">{formatPrice(item.price)}</span>
          {item.compareAtPrice > item.price && (
            <span className="text-xs text-ink-muted line-through">{formatPrice(item.compareAtPrice)}</span>
          )}
          {savings > 0 && (
            <span className="text-xs font-semibold text-emerald-600">Save {formatPrice(savings)}</span>
          )}
        </div>

        {/* Variant info */}
        {item.variants?.length > 1 && (
          <p className="text-[11px] text-ink-muted">
            {item.variants.length} variants available
          </p>
        )}

        {/* Add to Cart */}
        <button
          onClick={handleAddToCart}
          disabled={isOOS || addState === 'adding'}
          className={`mt-1 w-full sm:w-auto sm:self-start rounded-full text-sm font-semibold
            px-5 py-2 flex items-center justify-center gap-2 transition-all duration-200 border-2 active:scale-[.97]
            ${isOOS
              ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
              : addState === 'done'
              ? 'border-emerald-500 bg-emerald-500 text-white'
              : 'border-primary text-primary hover:bg-primary hover:text-white'
            }`}
        >
          {addState === 'adding' ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Adding…
            </>
          ) : addState === 'done' ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
              </svg>
              Added!
            </>
          ) : isOOS ? (
            'Out of Stock'
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              Add to Cart
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ── Empty State ───────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-24 text-center px-4">
    <div
      className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
      style={{ background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)' }}
    >
      <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
      </svg>
    </div>
    <h2 className="text-xl font-bold text-gray-900 mb-2">Your wishlist is empty</h2>
    <p className="text-ink-soft mb-8 max-w-sm text-sm leading-relaxed">
      Save items you love by tapping ♡ on any product. They'll appear here so you never lose track.
    </p>
    <Link to="/shop" className="btn-primary rounded-full px-8 py-3 font-semibold">
      Start Shopping →
    </Link>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const WishlistPage = ({ embedded = false }) => {
  const { items, removeFromWishlist } = useWishlist();
  const { addItem } = useCart();
  const [sortBy, setSortBy] = useState('recent');
  const [addingAll, setAddingAll] = useState(false);

  const sorted = [...items].sort((a, b) => {
    if (sortBy === 'price_asc')  return a.price - b.price;
    if (sortBy === 'price_desc') return b.price - a.price;
    return 0; // recent: keep original insertion order
  });

  const handleAddAllToCart = async () => {
    setAddingAll(true);
    for (const item of items) {
      const continueOOS = item.variants?.some(v => v.continueSellingWhenOutOfStock);
      const isOOS = (item.inventory ?? 0) <= 0 && !continueOOS;
      if (!isOOS) {
        addItem(item, item.variants?.[0]?.size || 'default');
        await new Promise(r => setTimeout(r, 80));
      }
    }
    toast.success(`${items.length} item${items.length !== 1 ? 's' : ''} added to cart!`, {
      icon: '🛒',
    });
    setAddingAll(false);
  };

  const inStockCount = items.filter(item => {
    const continueOOS = item.variants?.some(v => v.continueSellingWhenOutOfStock);
    return (item.inventory ?? 0) > 0 || continueOOS;
  }).length;

  return (
    <>
      {!embedded && <Helmet><title>My Wishlist — Skinn Thrive</title></Helmet>}

      {/* Hero header — only on standalone /wishlist page */}
      {!embedded && (
        <div
          className="relative overflow-hidden py-12 md:py-16 text-center"
          style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #7C3AED 60%, #A855F7 100%)' }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 blur-3xl"
            style={{ background: '#fff', transform: 'translate(30%, -30%)' }} />
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10 blur-2xl"
            style={{ background: '#fff', transform: 'translate(-30%, 30%)' }} />
          <div className="relative z-10 px-4">
            <p className="text-xs font-bold uppercase tracking-widest text-purple-200 mb-2">
              Your Saved Items
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              My Wishlist
            </h1>
            {items.length > 0 && (
              <p className="text-purple-200 text-sm">
                {items.length} item{items.length !== 1 ? 's' : ''} saved for later
              </p>
            )}
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-3xl mx-auto px-4 sm:px-6 py-8'}>
        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <span className="text-sm text-ink-soft">
                  <span className="font-semibold text-ink">{items.length}</span>{' '}
                  item{items.length !== 1 ? 's' : ''} saved
                  {inStockCount < items.length && (
                    <span className="text-ink-muted ml-1">
                      · {items.length - inStockCount} out of stock
                    </span>
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {inStockCount > 0 && (
                  <button
                    onClick={handleAddAllToCart}
                    disabled={addingAll}
                    className="btn-primary btn-sm rounded-full px-4 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {addingAll ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Adding…
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                        </svg>
                        Add all to cart
                      </>
                    )}
                  </button>
                )}

                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="text-xs font-medium text-ink border border-border rounded-xl px-3 py-1.5 bg-white
                             focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 cursor-pointer"
                >
                  {SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Item list */}
            <div className="space-y-3">
              {sorted.map(item => (
                <WishlistCard
                  key={item.id}
                  item={item}
                  onRemove={removeFromWishlist}
                />
              ))}
            </div>

            {/* Footer CTA */}
            <div className="text-center mt-10 pt-8 border-t border-border">
              <p className="text-ink-soft text-sm mb-4">Looking for more?</p>
              <Link to="/shop" className="btn-outline rounded-full px-8">
                Continue Shopping →
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default WishlistPage;
